import { useQuery } from '@tanstack/react-query'

import type { ChatStreamState } from '@/features/chat-stream/types'

import { messagesApi } from '../api/messagesApi'
import type { MyChatSummary } from '../types/messages.types'

export const myChatsKeys = {
  list: ['messages', 'my-chats'] as const,
}

/**
 * Lista de chats del usuario. Polling cada 30s para que los badges de unread
 * suban casi en tiempo real sin saturar (el reader interno ya hace 20s sobre
 * el chat seleccionado vía useChatMessagesQuery).
 *
 * <p>Refetch también al volver al tab — combinado con el polling cubre tanto
 * el caso "estoy con la app abierta sin tocar nada" como "vuelvo de otra pestaña".</p>
 *
 * <p>Si se pasa {@code streamState} y el SSE está sano (OPEN, CONNECTING,
 * RECONNECTING, CLOSED, DISABLED), desactivamos el polling — el stream invalida
 * la query en cada NEW_MESSAGE. Solo polleamos en POLLING_FALLBACK o cuando no
 * se pasa estado (backward compat).</p>
 */
export function useMyChatsQuery(streamState?: ChatStreamState) {
  const shouldPoll = streamState === undefined || streamState === 'POLLING_FALLBACK'
  return useQuery<MyChatSummary[]>({
    queryKey: myChatsKeys.list,
    queryFn: () => messagesApi.listMyChats(),
    refetchInterval: shouldPoll ? 30_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })
}
